import React, {useEffect, useMemo, useRef, useState} from "react";
import Popup from "reactjs-popup";
import "reactjs-popup/dist/index.css";
import Styles from "./styles.module.scss";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

import uploadIcon from '../../../theme/assets/svg/tc_cab_capsule_upload_icon.svg';
import photoIcon from '../../../theme/assets/svg/tc_cab_capsule_file_photo_icon.svg';
import videoIcon from '../../../theme/assets/svg/tc_cab_capsule_file_video_icon.svg';
import audioIcon from '../../../theme/assets/svg/tc_cab_capsule_file_audio_icon.svg';
import docIcon from '../../../theme/assets/svg/tc_cab_capsule_file_doc_icon.svg';

import {
    deleteCapsuleFile,
    getCapsuleFiles,
    setCapsuleFilesModalStatus,
    setStorageModalStatus,
    uploadCapsuleFile,
    uploadCapsuleFileFailure
} from "../../../store/cabinet/action";
import {
    fileSizeHandler,
    isCapsuleEditable,
    pluralHandler,
    storageHandler,
    storageSizeHandler
} from "../../../helpers/capsule";

const TYPE_ICON = {
    photo: photoIcon,
    video: videoIcon,
    audio: audioIcon,
    document: docIcon
};

const CabinetCapsuleFilesModal = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const capsuleFilesModalStatus = useSelector(store => store.cabinet.capsuleFilesModalStatus);
    const capsuleFiles = useSelector(store => store.cabinet.capsuleFiles);
    const capsuleStats = useSelector(store => store.cabinet.capsuleStats);
    const uploadCapsuleFileStarted = useSelector(store => store.cabinet.uploadCapsuleFileStarted);
    const uploadProgress = useSelector(store => store.cabinet.capsuleFileUploadProgress);
    const uploadErr = useSelector(store => store.cabinet.uploadCapsuleFileErr);

    const [filter, setFilter] = useState('all');
    // сервер по умолчанию сжимает; оставляем выбор пользователю
    const [compress, setCompress] = useState(true);
    const [isDragOver, setIsDragOver] = useState(false);
    const [localErr, setLocalErr] = useState('');

    const inputRef = useRef(null);

    const files = useMemo(()=> capsuleFiles?.files || [], [capsuleFiles]);
    const limits = capsuleFiles?.limits;
    const isEditable = isCapsuleEditable(capsuleStats?.capsule_status);

    const filters = useMemo(()=>([
        {key: 'all', name: t('Все')},
        {key: 'photo', name: t('Фото')},
        {key: 'video', name: t('Видео')},
        {key: 'audio', name: t('Аудио')},
        {key: 'document', name: t('Документы')}
    ]), [t]);

    const visibleFiles = useMemo(()=>{
        if (filter === 'all'){
            return files;
        }
        return files.filter(file => file?.type === filter);
    }, [files, filter]);

    // с API 16 количество файлов не ограничено — ограничен объём хранилища
    const readyCount = files.filter(file => file?.status === 'ready').length;
    const unfinishedCount = files.filter(file => file?.status === 'pending_upload' || file?.status === 'failed').length;
    const storage = useMemo(
        ()=> storageHandler(capsuleFiles?.storage, capsuleStats?.storage),
        [capsuleFiles, capsuleStats]
    );
    const isLimitReached = storage.isFull;
    // размер сжатого файла приезжает позже — пока есть очередь, подтягиваем список
    const hasQueued = files.some(file => file?.compression === 'queued');

    useEffect(()=>{
        if (capsuleFilesModalStatus){
            dispatch(getCapsuleFiles());
        }
    },[capsuleFilesModalStatus, dispatch])

    // сжатие идёт в фоне и меняет размер файла — обновляем список, пока очередь
    // не разошлась. Без очереди запросов нет
    useEffect(()=>{
        if (!capsuleFilesModalStatus || !hasQueued){
            return;
        }
        const timer = setInterval(()=>{
            dispatch(getCapsuleFiles());
        }, 10000);
        return ()=>clearInterval(timer);
    },[capsuleFilesModalStatus, hasQueued, dispatch])

    const closeModal = () =>{
        setLocalErr('');
        dispatch(uploadCapsuleFileFailure(false));
        dispatch(setCapsuleFilesModalStatus(false));
    }

    // тип и размер проверяем до пресайна, чтобы не тратить запрос впустую
    const validateHandler = (file) =>{
        if (!limits?.mimes){
            return true;
        }

        const type = Object.keys(limits.mimes).find(key => (limits.mimes[key] || []).includes(file?.type));

        if (!type){
            setLocalErr(t('Этот тип файла загрузить нельзя'));
            return false;
        }

        const maxSizeMb = limits?.max_size_mb?.[type];
        if (maxSizeMb && file?.size > maxSizeMb * 1024 * 1024){
            setLocalErr(`${t('Файл больше допустимых')} ${maxSizeMb} MB`);
            return false;
        }

        // место считает и сервер, но лучше не гонять файл впустую
        if (storage.known && storage.quota && file?.size > storage.free){
            setLocalErr(`${t('Не хватает места: свободно')} ${storageSizeHandler(storage.free)}`);
            return false;
        }

        return true;
    }

    const uploadHandler = (fileList) =>{
        setLocalErr('');
        dispatch(uploadCapsuleFileFailure(false));

        const file = fileList && fileList[0];
        if (!file || !isEditable || uploadCapsuleFileStarted){
            return;
        }
        if (isLimitReached){
            setLocalErr(t('Хранилище заполнено — удалите что-нибудь, чтобы освободить место'));
            return;
        }
        if (!validateHandler(file)){
            return;
        }

        dispatch(uploadCapsuleFile(file, compress));
    }

    const errorText = localErr
        || uploadErr?.errors?.file?.[0]
        || uploadErr?.errors?.mime?.[0]
        || uploadErr?.errors?.size?.[0]
        || uploadErr?.message
        || '';

    return (
        <Popup
            open={capsuleFilesModalStatus}
            modal
            nested
            className="capsuleModal"
            closeOnDocumentClick
            onClose={closeModal}
        >
            <div className={`${Styles.modalContainer}`}>
                <div className={`${Styles.modalCloseBtn}`} onClick={closeModal}>
                    <div/>
                    <div/>
                </div>

                <div className={`${Styles.modalContentWrap}`}>
                    <div className={`${Styles.modalHead}`}>
                        <div className={`${Styles.modalTitle}`}>{t('Памятные материалы')}</div>
                        <div className={`${Styles.modalNote}`}>
                            {t('Ваш личный архив фото, видео, аудио и документов')}
                        </div>

                        {uploadCapsuleFileStarted && uploadProgress ? (
                            <div className={`${Styles.uploadWrap}`}>
                                <div className={`${Styles.uploadPos}`}>
                                    <div className={`${Styles.uploadName}`}>
                                        {t('Загрузка файла')}: <span>{uploadProgress?.name}</span>
                                    </div>
                                    <div className={`${Styles.uploadBar}`}>
                                        <div className={`${Styles.uploadFill}`} style={{width: `${uploadProgress?.percent || 0}%`}}/>
                                    </div>
                                    <div className={`${Styles.uploadMeta}`}>
                                        <span>{uploadProgress?.percent || 0}%</span>
                                        <span>{fileSizeHandler(uploadProgress?.loaded)} {t('из')} {fileSizeHandler(uploadProgress?.total)}</span>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div
                                className={`${Styles.dropZone} ${isDragOver ? Styles.dropZoneActive : null} ${!isEditable || isLimitReached ? Styles.dropZoneDsb : null}`}
                                onClick={()=>{
                                    if (isEditable && !isLimitReached && inputRef.current){
                                        inputRef.current.click();
                                    }
                                }}
                                onDragOver={(e)=>{e.preventDefault(); setIsDragOver(true);}}
                                onDragLeave={()=>{setIsDragOver(false)}}
                                onDrop={(e)=>{
                                    e.preventDefault();
                                    setIsDragOver(false);
                                    uploadHandler(e.dataTransfer?.files);
                                }}
                            >
                                <div className={`${Styles.dropZoneName}`}>
                                    {t('Перетащите файлы сюда')}
                                    <span>{t('или загрузите с устройства')}</span>
                                </div>
                                <div className={`${Styles.dropZoneIconWrap}`}>
                                    <img src={uploadIcon} alt=""/>
                                </div>
                                <input
                                    className={`${Styles.dropZoneInput}`}
                                    type="file"
                                    ref={inputRef}
                                    onChange={(e)=>{
                                        uploadHandler(e.target.files);
                                        e.target.value = '';
                                    }}
                                />
                            </div>
                        )}

                        {isEditable ? (
                            <div
                                className={`${Styles.compressWrap}`}
                                onClick={()=>{setCompress(prev => !prev)}}
                            >
                                <span className={`${Styles.compressBox} ${compress ? Styles.compressBoxOn : ''}`}/>
                                <span className={`${Styles.compressName}`}>
                                    {t('Сжимать файлы, чтобы занимали меньше места')}
                                </span>
                            </div>
                        ) : null}

                        <div className={`${Styles.tcErr} ${errorText ? Styles.tcErrActive : null}`}>{errorText}</div>

                        <div className={`${Styles.filtersWrap}`}>
                            {filters.map((item)=>(
                                <div
                                    className={`${Styles.filterItem} ${filter === item.key ? Styles.filterItemActive : null}`}
                                    key={'capsule-file-filter_' + item.key}
                                    onClick={()=>{setFilter(item.key)}}
                                >
                                    {item.name}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className={`${Styles.modalBody}`}>
                        {visibleFiles.length ? (
                            <div className={`${Styles.filesGrid}`}>
                                {visibleFiles.map((file)=>(
                                    <div
                                        className={`${Styles.fileCard} ${file?.status === 'failed' ? Styles.fileCardErr : ''} ${file?.status === 'pending_upload' ? Styles.fileCardWarn : ''}`}
                                        key={'capsule-file_' + file?.id}
                                        title={file?.error || ''}
                                    >
                                        {isEditable ? (
                                            <div
                                                className={`${Styles.fileCardRemove}`}
                                                title={t('Удалить')}
                                                onClick={()=>{dispatch(deleteCapsuleFile(file?.id))}}
                                            >
                                                <div/>
                                                <div/>
                                            </div>
                                        ) : null}

                                        {/* download_url отдаёт файл под исходным именем;
                                            ссылка подписанная и живёт около 30 минут */}
                                        {file?.status === 'ready' && (file?.download_url || file?.url) ? (
                                            <a
                                                className={`${Styles.fileCardDownload}`}
                                                href={file.download_url || file.url}
                                                title={t('Скачать')}
                                                target='_blank'
                                                rel='noreferrer'
                                                download
                                            >
                                                <span/>
                                            </a>
                                        ) : null}

                                        <div className={`${Styles.fileCardIconWrap}`}>
                                            {file?.type === 'photo' && file?.status === 'ready' && file?.url ? (
                                                <img className={`${Styles.fileCardPreview}`} src={file.url} alt={file?.name}/>
                                            ) : (
                                                <img src={TYPE_ICON[file?.type] || docIcon} alt=""/>
                                            )}
                                        </div>

                                        <div className={`${Styles.fileCardName}`}>{file?.name}</div>

                                        {file?.status === 'failed' ? (
                                            <div className={`${Styles.fileCardStatus} ${Styles.fileCardStatusErr}`}>{t('Ошибка загрузки')}</div>
                                        ) : null}
                                        {file?.status === 'pending_upload' ? (
                                            <div className={`${Styles.fileCardStatus} ${Styles.fileCardStatusWarn}`}>
                                                {t('Загрузка не завершена')}
                                            </div>
                                        ) : null}
                                        {file?.status === 'ready' ? (
                                            <div className={`${Styles.fileCardStatus}`}>
                                                {fileSizeHandler(file?.size)}
                                                {file?.compression === 'queued' ? ` · ${t('сжимаем')}` : ''}
                                            </div>
                                        ) : null}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className={`${Styles.emptyNote}`}>{t('Данных пока нет')}</div>
                        )}
                    </div>

                    <div className={`${Styles.modalFooter}`}>
                        <div className={`${Styles.modalFooterName}`}>
                            <span>
                                {readyCount} {pluralHandler(readyCount, [t('файл'), t('файла'), t('файлов')])}
                                {storage.known && storage.quota
                                    ? ` · ${storageSizeHandler(storage.used)} ${t('из')} ${storageSizeHandler(storage.quota)}`
                                    : ''}
                            </span>
                            {storage.known && storage.quota ? (
                                <span className={`${Styles.storageBar}`}>
                                    <span className={`${Styles.storageFill}`} style={{width: `${storage.percent}%`}}/>
                                </span>
                            ) : null}
                            {/* место продаётся пакетами на срок; запечатанной
                                капсуле докупать нечего — файлов больше не будет */}
                            {isEditable ? (
                                <span
                                    className={`${Styles.storageBuy}`}
                                    onClick={()=>{dispatch(setStorageModalStatus(true))}}
                                >
                                    {t('Больше места')}
                                </span>
                            ) : null}
                            {unfinishedCount ? (
                                <span className={`${Styles.modalFooterHint}`}>
                                    {t('Не загрузилось')}: {unfinishedCount} — {t('удалите и загрузите снова')}
                                </span>
                            ) : null}
                        </div>
                        <div
                            className={`${Styles.modalBtn} ${!isEditable || isLimitReached || uploadCapsuleFileStarted ? Styles.modalBtnDsb : null}`}
                            onClick={()=>{
                                if (isEditable && !isLimitReached && !uploadCapsuleFileStarted && inputRef.current){
                                    inputRef.current.click();
                                }
                            }}
                        >
                            {t('Загрузить файлы')}
                        </div>
                    </div>
                </div>
            </div>
        </Popup>
    );
};

export default CabinetCapsuleFilesModal;
